import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { leadAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Users, TrendingUp, Calendar, ArrowUpRight, 
  Phone, Clock, Target, Plus, HardHat, MapPin, Camera,
  MessageSquare, PhoneMissed, Sparkles, X
} from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { OnboardingChecklist } from '@/components/onboarding/OnboardingChecklist';
import { TestReceptionistButton } from '@/components/onboarding/TestReceptionistButton';
import { BuyExtraCreditsButton } from '@/components/BuyExtraCreditsButton';
import { jobCategoryLabel } from '@/lib/jobCategory';
import { formatHoursDecimal } from '@/lib/formatHours';
import {
  quotaWarningLevel, quotaWarningLabel, formatPeriod, upgradeCtaLabel, upgradeTarget,
} from '@/lib/aiQuota';
import { useNavigate } from 'react-router-dom';

export function DashboardPage() {
  const { workspace, user } = useAuth();
  const { t, language } = useLanguage();
  const tf = (fr, en) => (language === 'fr' ? fr : en);
  const [stats, setStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fieldActivities, setFieldActivities] = useState([]);
  const [recentFieldPhotos, setRecentFieldPhotos] = useState([]);
  const [demoActivities, setDemoActivities] = useState([]);
  const [checklistKey, setChecklistKey] = useState(0);
  const [hoursByCategory, setHoursByCategory] = useState([]);
  const [aiQuota, setAiQuota] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [statsRes, leadsRes] = await Promise.all([
        leadAPI.getStats(),
        leadAPI.list({ limit: 5 }),
      ]);
      setStats(statsRes.data);
      setRecentLeads(leadsRes.data.leads);

      // Seed demo data (idempotent) if the workspace has zero real leads,
      // then load the demo activity cards (SMS + missed call).
      const totalLeads = statsRes.data?.total_leads ?? 0;
      if (totalLeads === 0) {
        try { await api.post('/onboarding/seed-demo'); } catch { /* ignore */ }
        // Refetch leads so the demo lead shows up
        try {
          const refetched = await leadAPI.list({ limit: 5 });
          setRecentLeads(refetched.data.leads);
        } catch { /* ignore */ }
      }
      try {
        const { data } = await api.get('/onboarding/demo-activities');
        setDemoActivities(data.activities || []);
      } catch { /* ignore */ }
    } catch (err) {
      console.error('Error loading dashboard:', err);
    } finally {
      setIsLoading(false);
    }
    // Load field activities
    try {
      const { data } = await api.get('/mobile/admin/activity');
      setFieldActivities(data.activities || []);
      setRecentFieldPhotos(data.recent_photos || []);
    } catch { /* not critical */ }
    // Load hours-by-category for this week (best-effort, requires multi_user feature)
    try {
      const { data } = await api.get('/timetracking/hours-by-category');
      setHoursByCategory(data.buckets || []);
    } catch { /* feature may be gated by plan — silent */ }
    // Load AI quota snapshot (read-only, never raises)
    try {
      const { data } = await api.get('/seo/quota');
      setAiQuota(data || null);
    } catch { /* ignore */ }
  };

  const dismissDemoActivity = async (activityId) => {
    setDemoActivities((prev) => prev.filter((a) => a.activity_id !== activityId));
    try { await api.delete(`/onboarding/demo-activities/${activityId}`); }
    catch { /* ignore */ }
  };

  const handleReceptionistTested = async () => {
    // Reload leads + checklist after the test ran
    try {
      const [statsRes, leadsRes] = await Promise.all([
        leadAPI.getStats(),
        leadAPI.list({ limit: 5 }),
      ]);
      setStats(statsRes.data);
      setRecentLeads(leadsRes.data.leads);
    } catch { /* ignore */ }
    setChecklistKey((k) => k + 1);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const locale = language === 'fr' ? 'fr-CA' : 'en-CA';
    return date.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusLabel = (status) => t(`status.${status}`);
  const getSourceLabel = (source) => t(`source.${source}`);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const STATUS_KEYS = ['new', 'contacted', 'appointment', 'quote_sent', 'won', 'lost'];
  const SOURCE_KEYS = ['maps', 'ads', 'seo', 'direct', 'referral', 'other'];

  return (
    <div className="space-y-6 md:space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">
            {tf(`Bienvenue chef${user?.name ? ', ' + user.name.split(' ')[0] : ''} !`, `Welcome boss${user?.name ? ', ' + user.name.split(' ')[0] : ''}!`)}
          </h1>
          <p className="text-muted-foreground mt-1">
            {tf(
              `Votre bureau virtuel ${workspace?.name ? `« ${workspace.name} » ` : ''}est pret. Voici votre journee.`,
              `Your virtual office ${workspace?.name ? `"${workspace.name}" ` : ''}is ready. Here is your day.`
            )}
          </p>
        </div>
        <div className="flex gap-3">
          <Button asChild variant="outline" data-testid="new-form-btn">
            <Link to="/forms/new">
              <Plus className="mr-2 h-4 w-4" />
              {tf('Nouveau formulaire', 'New form')}
            </Link>
          </Button>
          <Button asChild data-testid="view-leads-btn">
            <Link to="/leads">
              {tf('Voir mes prospects', 'See my leads')}
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>

      {/* Onboarding checklist (auto-hides when 100%) */}
      <OnboardingChecklist refreshKey={checklistKey} />

      {/* WOW button — Test my AI receptionist */}
      <TestReceptionistButton onTested={handleReceptionistTested} />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="hover:shadow-md transition-shadow" data-testid="stat-total-leads">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('dashboard.totalLeads')}
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black font-heading">
              {stats?.total_leads || 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {t('dashboard.allTime')}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow" data-testid="stat-month-leads">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('dashboard.thisMonth')}
            </CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black font-heading text-primary">
              {stats?.leads_this_month || 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {t('dashboard.newLeads')}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow" data-testid="stat-conversion">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('dashboard.conversionRate')}
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black font-heading text-green-600">
              {stats?.conversion_rate || 0}%
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {t('dashboard.leadsWon')}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow" data-testid="stat-pending">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('dashboard.pending')}
            </CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black font-heading text-amber-600">
              {(stats?.leads_by_status?.new || 0) + (stats?.leads_by_status?.contacted || 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {t('dashboard.toProcess')}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* AI generations monthly quota */}
      {aiQuota && aiQuota.total > 0 && (() => {
        const level = quotaWarningLevel(aiQuota.used, aiQuota.total);
        const warnLabel = quotaWarningLabel(level, language);
        const pct = aiQuota.total > 0 ? Math.min((aiQuota.used / aiQuota.total) * 100, 100) : 0;
        const barColor =
          level === 'reached' ? 'bg-destructive' :
          level === 'warn90'  ? 'bg-orange-500' :
          level === 'warn80'  ? 'bg-amber-500'  :
          'bg-primary';
        const ctaLabel = upgradeCtaLabel(aiQuota.plan, language);
        const target = upgradeTarget(aiQuota.plan);
        return (
          <Card data-testid="ai-quota-card">
            <CardHeader>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <CardTitle className="text-lg font-bold flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-primary" />
                    {tf('Générations IA', 'AI Generations')}
                    <span className="text-xs font-normal text-muted-foreground">
                      ({formatPeriod(aiQuota.period, language)})
                    </span>
                  </CardTitle>
                  <div className="text-2xl font-bold tabular-nums mt-1" data-testid="ai-quota-used">
                    {aiQuota.used} <span className="text-base font-normal text-muted-foreground">/ {aiQuota.total} {tf('utilisées ce mois', 'used this month')}</span>
                  </div>
                  {aiQuota.extra > 0 && (
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {tf('Inclut', 'Includes')} {aiQuota.extra} {tf('extra accordées', 'extra granted')}
                    </div>
                  )}
                  {aiQuota.purchased > 0 && (
                    <div className="text-xs text-muted-foreground mt-0.5" data-testid="ai-quota-purchased">
                      {tf('Inclut', 'Includes')} {aiQuota.purchased} {tf('crédits achetés (non-expirables)', 'purchased credits (non-expiring)')}
                    </div>
                  )}
                </div>
                {warnLabel && (
                  <Badge
                    variant={level === 'reached' ? 'destructive' : 'secondary'}
                    className="text-xs"
                    data-testid="ai-quota-badge"
                  >
                    {warnLabel}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${barColor}`}
                  style={{ width: `${pct}%` }}
                  data-testid="ai-quota-bar"
                />
              </div>
              {level === 'reached' && (
                <p className="text-sm text-destructive" data-testid="ai-quota-reached-msg">
                  {tf('Quota IA atteint pour ce mois.', 'AI quota reached for this month.')}
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                {target && (
                  <Button
                    size="sm"
                    variant={level === 'reached' ? 'default' : 'outline'}
                    onClick={() => navigate('/billing')}
                    data-testid="ai-quota-upgrade-cta"
                  >
                    <ArrowUpRight className="h-4 w-4 mr-1.5" />
                    {ctaLabel}
                  </Button>
                )}
                <BuyExtraCreditsButton
                  quota={aiQuota}
                  variant={level === 'reached' && !target ? 'default' : 'outline'}
                  size="sm"
                />
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Hours by job category (this week) */}
      {hoursByCategory.length > 0 && hoursByCategory.some((b) => b.hours > 0) && (
        <Card data-testid="hours-by-category-card">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <HardHat className="h-5 w-5 text-primary" />
              {tf('Heures par type de chantier', 'Hours by worksite type')}
              <span className="text-xs font-normal text-muted-foreground ml-1">
                ({tf('cette semaine', 'this week')})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="divide-y" data-testid="hours-by-category-list">
              {hoursByCategory
                .filter((b) => b.hours > 0)
                .sort((a, b) => b.hours - a.hours)
                .map((b) => {
                  const label = b.job_category === 'unset'
                    ? tf('Non défini', 'Not set')
                    : jobCategoryLabel(b.job_category, language);
                  const display = formatHoursDecimal(b.hours);
                  return (
                    <li
                      key={b.job_category}
                      className="flex items-center justify-between py-2.5 text-sm"
                      data-testid={`hours-cat-${b.job_category}`}
                    >
                      <span>{label}</span>
                      <span className="font-bold font-heading tabular-nums">{display}</span>
                    </li>
                  );
                })}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Pipeline Overview & Recent Leads */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pipeline Status */}
        <Card className="lg:col-span-1" data-testid="pipeline-overview">
          <CardHeader>
            <CardTitle className="text-lg font-bold">{t('dashboard.pipeline')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {STATUS_KEYS.map((status) => (
              <div key={status} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${
                    status === 'new' ? 'bg-blue-500' :
                    status === 'contacted' ? 'bg-yellow-500' :
                    status === 'appointment' ? 'bg-purple-500' :
                    status === 'quote_sent' ? 'bg-orange-500' :
                    status === 'won' ? 'bg-green-500' :
                    'bg-red-500'
                  }`} />
                  <span className="text-sm">{getStatusLabel(status)}</span>
                </div>
                <span className="font-bold font-heading">
                  {stats?.leads_by_status?.[status] || 0}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Recent Leads */}
        <Card className="lg:col-span-2" data-testid="recent-leads">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-bold">{t('dashboard.recentLeads')}</CardTitle>
            <Button asChild variant="ghost" size="sm">
              <Link to="/leads">{t('dashboard.viewAll')}</Link>
            </Button>
          </CardHeader>
          <CardContent>
            {recentLeads.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>{t('dashboard.noLeadsYet')}</p>
                <p className="text-sm">{t('dashboard.createFormPrompt')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {recentLeads.map((lead) => (
                  <Link
                    key={lead.lead_id}
                    to={`/leads/${lead.lead_id}`}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="font-bold text-primary">
                          {lead.name?.charAt(0).toUpperCase() || '?'}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold">{lead.name}</p>
                          {lead.is_demo && (
                            <Badge
                              className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] px-1.5 py-0 h-4"
                              data-testid={`demo-badge-${lead.lead_id}`}
                            >
                              {tf('Demo', 'Demo')}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          {lead.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {lead.phone}
                            </span>
                          )}
                          {lead.service && (
                            <span>{lead.service}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge 
                        variant="secondary" 
                        className={`status-${lead.status}`}
                      >
                        {getStatusLabel(lead.status)}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        <Clock className="h-3 w-3 inline mr-1" />
                        {formatDate(lead.created_at)}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Demo activities (SMS + missed call cards) -- shown until dismissed */}
      {demoActivities.length > 0 && (
        <Card data-testid="demo-activities-card">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-500" />
              {tf('Apercu de votre future activite', 'Preview of your activity')}
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {demoActivities.map((act) => {
              const isSms = act.type === 'sms';
              const Icon = isSms ? MessageSquare : PhoneMissed;
              return (
                <div
                  key={act.activity_id}
                  className={`relative flex items-start gap-3 p-4 rounded-xl border ${
                    isSms ? 'bg-blue-50/50 border-blue-200' : 'bg-red-50/50 border-red-200'
                  }`}
                  data-testid={`demo-activity-${act.type}`}
                >
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                    isSms ? 'bg-blue-500 text-white' : 'bg-red-500 text-white'
                  }`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold text-sm">{act.title}</p>
                      <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] px-1.5 py-0 h-4">
                        {tf('Demo', 'Demo')}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 truncate">{act.preview}</p>
                    {act.phone && (
                      <p className="text-xs font-mono text-muted-foreground mt-1">{act.phone}</p>
                    )}
                  </div>
                  <button
                    onClick={() => dismissDemoActivity(act.activity_id)}
                    className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
                    aria-label={tf('Masquer', 'Dismiss')}
                    type="button"
                    data-testid={`demo-dismiss-${act.activity_id}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Sources Overview */}
      <Card data-testid="sources-overview">
        <CardHeader>
          <CardTitle className="text-lg font-bold">{t('dashboard.leadSources')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {SOURCE_KEYS.map((source) => (
              <div 
                key={source} 
                className="text-center p-4 rounded-lg bg-muted/30"
              >
                <div className="text-2xl font-black font-heading">
                  {stats?.leads_by_source?.[source] || 0}
                </div>
                <p className="text-sm text-muted-foreground">{getSourceLabel(source)}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      {stats?.total_leads === 0 && (
        <Card className="bg-primary/5 border-primary/20" data-testid="quick-start">
          <CardContent className="py-8">
            <div className="text-center">
              <h3 className="font-heading text-xl font-bold mb-2">
                {t('dashboard.quickStart')}
              </h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                {t('dashboard.quickStartDesc')}
              </p>
              <div className="flex justify-center gap-4">
                <Button asChild data-testid="create-first-form-btn">
                  <Link to="/forms/new">
                    <Plus className="mr-2 h-4 w-4" />
                    {t('dashboard.createForm')}
                  </Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/landing-pages">
                    {t('dashboard.createLandingPage')}
                  </Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Field Activity */}
      {fieldActivities.length > 0 && (
        <Card data-testid="field-activity-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HardHat className="h-5 w-5 text-orange-500" />
              {language === 'fr' ? 'Activite terrain en temps reel' : 'Real-time field activity'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {fieldActivities.map(a => {
                const isClockedIn = a.clock_in && !a.clock_out;
                const duration = isClockedIn
                  ? Math.round((Date.now() - new Date(a.clock_in).getTime()) / 60000)
                  : a.clock_out ? Math.round((new Date(a.clock_out).getTime() - new Date(a.clock_in).getTime()) / 60000) : 0;
                const hrs = Math.floor(duration / 60);
                const mins = duration % 60;
                return (
                  <div key={a.activity_id} className={`flex items-center justify-between p-3 rounded-lg ${isClockedIn ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-2.5 h-2.5 rounded-full ${isClockedIn ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                      <div>
                        <p className="font-medium text-sm">{a.employee_name}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <MapPin className="h-3 w-3" />{a.client_name}{a.client_address ? ` - ${a.client_address}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{hrs > 0 ? `${hrs}h ` : ''}{mins}min</p>
                      <Badge className={isClockedIn ? 'bg-green-500 text-white text-[10px]' : 'bg-gray-200 text-gray-600 text-[10px]'}>
                        {isClockedIn ? (language === 'fr' ? 'Sur place' : 'On site') : (language === 'fr' ? 'Termine' : 'Done')}
                      </Badge>
                      {a.photos?.length > 0 && (
                        <span className="ml-2 text-xs text-muted-foreground"><Camera className="h-3 w-3 inline" /> {a.photos.length}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {recentFieldPhotos.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-xs font-medium text-muted-foreground mb-2">{language === 'fr' ? 'Dernieres photos terrain' : 'Recent field photos'}</p>
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {recentFieldPhotos.map(p => (
                    <img key={p.photo_id} src={`${window.location.origin}${p.url}`} alt="" className="w-16 h-16 object-cover rounded-lg shrink-0" />
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
